/**
 * RelatorioDISC - Componente para gerar relatório PDF do teste DISC
 * Segue modelo oficial de relatório comportamental
 */
import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  FileText, 
  Download, 
  Loader2, 
  User,
  Briefcase,
  Calendar,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Perfis DISC com descrições completas
const DISC_PROFILES = {
  D: {
    nome: 'Dominante',
    cor: '#dc2626',
    caracteristicas: 'Direto, voltado para resultados, que toma iniciativas e não tem medo de correr riscos. Gosta de autonomia e de superar metas. É competitivo e focado em objetivos.',
    pontosFortes: ['Foco em resultados', 'Liderança natural', 'Resolução rápida de problemas', 'Orientação para metas'],
    pontosAtencao: ['Pode parecer impaciente', 'Necessidade de delegar mais', 'Atenção aos detalhes', 'Ouvir mais os outros'],
    comunicacao: 'Direto e objetivo, prefere ir direto ao ponto. Gosta de comunicação clara e sem rodeios.',
    feedback: 'Feedback claro, focado em resultados futuros e sem rodeios. Aprecia reconhecimento por conquistas.',
    ambiente: 'Ambiente dinâmico, desafiador, com autonomia para tomar decisões e metas claras.',
    contribuicao: 'Impulsiona o alcance de metas e assume a liderança em momentos de crise ou quando é preciso tomar decisões rápidas.'
  },
  I: {
    nome: 'Influente',
    cor: '#f59e0b',
    caracteristicas: 'Comunicativo, entusiasta e otimista. Gosta de interagir com pessoas e criar um ambiente positivo. É persuasivo e tem facilidade para motivar equipes.',
    pontosFortes: ['Comunicação persuasiva', 'Criatividade', 'Entusiasmo contagiante', 'Habilidade de networking'],
    pontosAtencao: ['Foco nos detalhes', 'Gerenciamento de tempo', 'Finalização de tarefas', 'Análise mais criteriosa'],
    comunicacao: 'Expressivo e entusiasta, gosta de contar histórias e engajar as pessoas na conversa.',
    feedback: 'Prefere feedback positivo e construtivo, em ambiente informal. Valoriza reconhecimento público.',
    ambiente: 'Ambiente colaborativo, com liberdade para expressar ideias e interagir com diferentes pessoas.',
    contribuicao: 'Energiza a equipe, promove colaboração e ajuda a criar um ambiente de trabalho positivo e motivador.'
  },
  S: {
    nome: 'Estável',
    cor: '#22c55e',
    caracteristicas: 'Paciente, confiável e bom ouvinte. Valoriza a harmonia e a estabilidade. É leal e prefere ambientes previsíveis com relacionamentos duradouros.',
    pontosFortes: ['Paciência e persistência', 'Trabalho em equipe', 'Confiabilidade', 'Suporte aos colegas'],
    pontosAtencao: ['Adaptação a mudanças', 'Assertividade', 'Tomar iniciativa', 'Lidar com conflitos'],
    comunicacao: 'Calmo e paciente, prefere ouvir antes de falar. Comunicação gentil e empática.',
    feedback: 'Prefere feedback gentil e construtivo, em ambiente privado. Precisa de tempo para processar mudanças.',
    ambiente: 'Ambiente estável, previsível, com boas relações interpessoais e processos bem definidos.',
    contribuicao: 'Mantém a coesão da equipe, oferece suporte constante e garante a continuidade dos processos.'
  },
  C: {
    nome: 'Conforme',
    cor: '#3b82f6',
    caracteristicas: 'Analítico, preciso e focado em qualidade. Valoriza regras, procedimentos e exatidão. É sistemático e busca a perfeição em suas entregas.',
    pontosFortes: ['Atenção aos detalhes', 'Análise crítica', 'Organização', 'Padrão de qualidade elevado'],
    pontosAtencao: ['Flexibilidade', 'Velocidade de decisão', 'Aceitar imperfeições', 'Delegar tarefas'],
    comunicacao: 'Objetivo e baseado em fatos, prefere comunicação escrita e documentada.',
    feedback: 'Prefere feedback específico, baseado em dados e evidências. Valoriza precisão e detalhes.',
    ambiente: 'Ambiente organizado, com processos claros, tempo para análise e padrões de qualidade definidos.',
    contribuicao: 'Garante qualidade e precisão nas entregas, identifica riscos e propõe melhorias nos processos.'
  }
};

// Componente do relatório para renderização
const RelatorioContent = React.forwardRef(({ data, psicologo }, ref) => {
  const perfil = DISC_PROFILES[data.dominant];
  const scores = data.scores || { D: 0, I: 0, S: 0, C: 0 };
  
  return (
    <div ref={ref} className="bg-white p-8 text-black" style={{ width: '210mm', minHeight: '297mm', fontFamily: 'Arial, sans-serif' }}>
      {/* Cabeçalho */}
      <div className="border-b-4 border-violet-600 pb-4 mb-6">
        <h1 className="text-2xl font-bold text-violet-900 text-center">
          Relatório de Avaliação de Perfil Comportamental
        </h1>
        <h2 className="text-lg text-violet-700 text-center mt-1">Método DISC</h2>
      </div>

      {/* 1. Dados do Avaliado */}
      <section className="mb-6">
        <h3 className="text-lg font-bold text-violet-800 border-b border-violet-300 pb-1 mb-3">
          1. Dados do Avaliado
        </h3>
        <table className="w-full">
          <tbody>
            <tr>
              <td className="py-1 font-semibold w-40">Nome:</td>
              <td className="py-1">{data.nome}</td>
            </tr>
            <tr>
              <td className="py-1 font-semibold">Cargo / Função:</td>
              <td className="py-1">{data.cargo || 'Não informado'}</td>
            </tr>
            <tr>
              <td className="py-1 font-semibold">Data da Avaliação:</td>
              <td className="py-1">{data.dataAvaliacao}</td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* 2. Gráficos de Perfil */}
      <section className="mb-6">
        <h3 className="text-lg font-bold text-violet-800 border-b border-violet-300 pb-1 mb-3">
          2. Gráficos de Perfil
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          O teste DISC analisa o comportamento através de quatro dimensões principais, representadas abaixo:
        </p>
        
        <div className="grid grid-cols-2 gap-4">
          {/* Barras de score */}
          <div className="space-y-3">
            {Object.entries(scores).map(([key, value]) => (
              <div key={key} className="flex items-center gap-2">
                <span className="w-24 font-semibold" style={{ color: DISC_PROFILES[key].cor }}>
                  {key} ({DISC_PROFILES[key].nome})
                </span>
                <div className="flex-1 bg-gray-200 rounded-full h-6 relative">
                  <div 
                    className="h-6 rounded-full flex items-center justify-end pr-2"
                    style={{ 
                      width: `${Math.max(value, 5)}%`, 
                      backgroundColor: DISC_PROFILES[key].cor 
                    }}
                  >
                    <span className="text-white text-sm font-bold">{value}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Legenda */}
          <div className="bg-gray-50 p-4 rounded-lg text-sm">
            <p><strong>D (Dominância):</strong> Foco em resultados e desafios.</p>
            <p><strong>I (Influência):</strong> Foco em pessoas e comunicação.</p>
            <p><strong>S (Estabilidade):</strong> Foco em colaboração e ritmo.</p>
            <p><strong>C (Conformidade):</strong> Foco em regras, precisão e qualidade.</p>
          </div>
        </div>
      </section>

      {/* 3. Perfil Comportamental Predominante */}
      <section className="mb-6">
        <h3 className="text-lg font-bold text-violet-800 border-b border-violet-300 pb-1 mb-3">
          3. Perfil Comportamental Predominante: {data.dominant} - {perfil.nome}
        </h3>
        
        <div className="mb-4">
          <h4 className="font-semibold text-gray-800">Características Principais:</h4>
          <p className="text-gray-700">{perfil.caracteristicas}</p>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-green-50 p-3 rounded-lg">
            <h4 className="font-semibold text-green-800 mb-2">Pontos Fortes:</h4>
            <ul className="list-disc list-inside text-sm text-green-700">
              {perfil.pontosFortes.map((ponto, i) => (
                <li key={i}>{ponto}</li>
              ))}
            </ul>
          </div>
          
          <div className="bg-amber-50 p-3 rounded-lg">
            <h4 className="font-semibold text-amber-800 mb-2">Pontos de Atenção:</h4>
            <ul className="list-disc list-inside text-sm text-amber-700">
              {perfil.pontosAtencao.map((ponto, i) => (
                <li key={i}>{ponto}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 4. Estilo de Comunicação */}
      <section className="mb-6">
        <h3 className="text-lg font-bold text-violet-800 border-b border-violet-300 pb-1 mb-3">
          4. Estilo de Comunicação
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h4 className="font-semibold text-gray-800">Como se comunica:</h4>
            <p className="text-gray-700 text-sm">{perfil.comunicacao}</p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-800">Como prefere receber feedback:</h4>
            <p className="text-gray-700 text-sm">{perfil.feedback}</p>
          </div>
        </div>
      </section>

      {/* 5. Ambiente de Trabalho Ideal */}
      <section className="mb-6">
        <h3 className="text-lg font-bold text-violet-800 border-b border-violet-300 pb-1 mb-3">
          5. Ambiente de Trabalho Ideal
        </h3>
        <p className="text-gray-700">{perfil.ambiente}</p>
      </section>

      {/* 6. Contribuição para a Equipe */}
      <section className="mb-8">
        <h3 className="text-lg font-bold text-violet-800 border-b border-violet-300 pb-1 mb-3">
          6. Contribuição para a Equipe
        </h3>
        <p className="text-gray-700">{perfil.contribuicao}</p>
      </section>

      {/* Rodapé com assinatura */}
      <footer className="mt-auto pt-8 border-t-2 border-gray-300">
        <div className="flex justify-between items-end">
          <div>
            <p className="text-sm text-gray-600">
              Relatório gerado em: {new Date().toLocaleDateString('pt-BR')} às {new Date().toLocaleTimeString('pt-BR')}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              G2eGente - Plataforma de Recrutamento Ético
            </p>
          </div>
          
          {psicologo && (
            <div className="text-right">
              <div className="border-t border-black w-64 mb-2"></div>
              <p className="font-semibold">{psicologo.nome}</p>
              {psicologo.crp && (
                <p className="text-sm text-gray-600">CRP: {psicologo.crp}</p>
              )}
            </div>
          )}
        </div>
      </footer>
    </div>
  );
});

RelatorioContent.displayName = 'RelatorioContent';

export default function RelatorioDISCModal({ 
  open, 
  onClose, 
  invite, 
  psychologists = [],
  crpObrigatorio = false
}) {
  const [loading, setLoading] = useState(false);
  const [psicologoSelecionado, setPsicologoSelecionado] = useState(null);
  const [nomeManual, setNomeManual] = useState('');
  const [crpManual, setCrpManual] = useState('');
  const [usarManual, setUsarManual] = useState(psychologists.length === 0);
  const reportRef = useRef(null);

  const resultado = invite?.resultado?.disc_detalhado || invite?.resultado;
  const rawDiscProfile = invite?.resultado?.disc_profile || resultado?.resultado_dominante;
  // Pegar apenas a primeira letra para o perfil dominante (ex: "DI" -> "D")
  const discProfile = rawDiscProfile ? rawDiscProfile[0] : null;
  
  if (!discProfile || !resultado) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resultado não disponível</DialogTitle>
          </DialogHeader>
          <p className="text-slate-500">
            O resultado do teste DISC não está disponível para este convite.
          </p>
          <DialogFooter>
            <Button onClick={onClose}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const dadosRelatorio = {
    nome: invite.nome_destinatario,
    cargo: invite.cargo_funcao || '',
    dataAvaliacao: invite.responded_at 
      ? new Date(invite.responded_at).toLocaleDateString('pt-BR', { 
          day: '2-digit', month: 'long', year: 'numeric' 
        })
      : new Date().toLocaleDateString('pt-BR'),
    dominant: discProfile,
    scores: resultado.scores || { D: 25, I: 25, S: 25, C: 25 }
  };

  const getPsicologoData = () => {
    if (usarManual) {
      if (!nomeManual.trim()) return null;
      return { nome: nomeManual.trim(), crp: crpManual.trim() || null };
    }
    if (psicologoSelecionado) {
      const psi = psychologists.find(p => p.id === psicologoSelecionado);
      return psi ? { nome: psi.nome, crp: psi.crp } : null;
    }
    return null;
  };

  const canGenerate = () => {
    const psi = getPsicologoData();
    if (!psi) return false;
    if (crpObrigatorio && !psi.crp) return false;
    return true;
  };

  const handleGenerate = async () => {
    const psicologo = getPsicologoData();
    if (!psicologo) {
      toast.error('Informe o responsável pelo relatório');
      return;
    }
    if (crpObrigatorio && !psicologo.crp) {
      toast.error('CRP é obrigatório para este relatório');
      return;
    }

    setLoading(true);
    try {
      // Renderizar o componente em um elemento oculto
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '0';
      tempDiv.style.width = '210mm';
      document.body.appendChild(tempDiv);

      // Usar React para renderizar
      const { createRoot } = await import('react-dom/client');
      const root = createRoot(tempDiv);
      
      // Timeout de segurança para evitar travamento
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Timeout na geração do PDF')), 30000);
      });
      
      const generatePromise = new Promise((resolve, reject) => {
        root.render(
          <RelatorioContent 
            ref={(el) => {
              if (el) {
                // Aguardar renderização completa
                requestAnimationFrame(async () => {
                  try {
                    const canvas = await html2canvas(el, {
                      scale: 1.5, // Reduzido de 2 para 1.5 para melhor performance
                      useCORS: true,
                      logging: false,
                      backgroundColor: '#ffffff',
                      allowTaint: true,
                      removeContainer: true,
                      imageTimeout: 5000,
                      onclone: (clonedDoc) => {
                        // Remover elementos desnecessários do clone
                        const clonedElement = clonedDoc.body.querySelector('[data-report-content]');
                        if (clonedElement) {
                          clonedElement.style.transform = 'none';
                        }
                      }
                    });
                    
                    const imgData = canvas.toDataURL('image/jpeg', 0.92); // JPEG é mais rápido que PNG
                    const pdf = new jsPDF('p', 'mm', 'a4');
                    const imgWidth = 210;
                    const imgHeight = (canvas.height * imgWidth) / canvas.width;
                    
                    pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight, undefined, 'FAST');
                    
                    // Salvar PDF
                    const fileName = `Relatorio_DISC_${invite.nome_destinatario.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
                    pdf.save(fileName);
                    
                    toast.success('Relatório PDF gerado com sucesso!');
                    resolve();
                  } catch (err) {
                    console.error('Erro ao gerar PDF:', err);
                    reject(err);
                  }
                });
              }
            }}
            data={dadosRelatorio}
            psicologo={psicologo}
          />
        );
      });

      // Usar race entre timeout e geração
      await Promise.race([generatePromise, timeoutPromise]);

      // Limpar
      root.unmount();
      document.body.removeChild(tempDiv);
      
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      toast.error('Erro ao gerar relatório');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-violet-600" />
            Gerar Relatório DISC
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Preview dos dados */}
          <div className="bg-slate-50 rounded-lg p-4">
            <h4 className="font-semibold text-slate-700 mb-3">Dados do Avaliado</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-slate-400" />
                <span><strong>Nome:</strong> {invite.nome_destinatario}</span>
              </div>
              <div className="flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-slate-400" />
                <span><strong>Cargo:</strong> {invite.cargo_funcao || 'Não informado'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span><strong>Data:</strong> {dadosRelatorio.dataAvaliacao}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-violet-500" />
                <span><strong>Perfil:</strong></span>
                <Badge className="bg-violet-100 text-violet-700">{rawDiscProfile} - {DISC_PROFILES[discProfile]?.nome}</Badge>
              </div>
            </div>
          </div>

          {/* Seleção de Psicólogo */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-slate-700">Responsável pelo Relatório</h4>
              {crpObrigatorio && (
                <Badge variant="outline" className="text-amber-600 border-amber-300">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  CRP Obrigatório
                </Badge>
              )}
            </div>

            {psychologists.length > 0 && (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Button
                    variant={!usarManual ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setUsarManual(false)}
                  >
                    Psicólogo Cadastrado
                  </Button>
                  <Button
                    variant={usarManual ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setUsarManual(true)}
                  >
                    Informar Manualmente
                  </Button>
                </div>
              </div>
            )}

            {!usarManual && psychologists.length > 0 ? (
              <div className="space-y-2">
                <Label>Selecione o Psicólogo</Label>
                <select
                  value={psicologoSelecionado || ''}
                  onChange={(e) => setPsicologoSelecionado(e.target.value)}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Selecione...</option>
                  {psychologists.map((psi) => (
                    <option key={psi.id} value={psi.id}>
                      {psi.nome} - CRP: {psi.crp}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome do Responsável *</Label>
                  <Input
                    value={nomeManual}
                    onChange={(e) => setNomeManual(e.target.value)}
                    placeholder="Nome completo"
                  />
                </div>
                <div className="space-y-2">
                  <Label>CRP {crpObrigatorio && '*'}</Label>
                  <Input
                    value={crpManual}
                    onChange={(e) => setCrpManual(e.target.value.toUpperCase())}
                    placeholder="Ex: 06/123456"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            onClick={handleGenerate}
            disabled={loading || !canGenerate()}
            className="bg-violet-600 hover:bg-violet-700"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Gerando...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Gerar PDF
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
